import { useState, useMemo, useEffect, useRef } from "react";

// ─── Inline skill data (759 skills: 5 CyberEye custom + 754 upstream) ───────
const CYBEREYE_CUSTOM_NAMES = new Set([
  "performing-cmmc-level2-assessment",
  "fedramp-authorization-preparation",
  "vendor-risk-management-program",
  "performing-hipaa-security-risk-analysis",
  "conducting-nist-800-171-assessment",
]);

const GRC_PRIORITY = new Set([
  "performing-cmmc-level2-assessment",
  "fedramp-authorization-preparation",
  "vendor-risk-management-program",
  "performing-hipaa-security-risk-analysis",
  "conducting-nist-800-171-assessment",
  "performing-nist-csf-maturity-assessment",
  "implementing-iso-27001-information-security-management",
  "performing-soc2-type2-audit-preparation",
  "implementing-pci-dss-compliance-controls",
  "implementing-gdpr-data-protection-controls",
  "implementing-gdpr-data-subject-access-request",
  "performing-privacy-impact-assessment",
  "implementing-nerc-cip-compliance-controls",
  "building-identity-governance-lifecycle-process",
  "implementing-identity-governance-with-sailpoint",
  "building-incident-response-playbook",
  "auditing-cloud-with-cis-benchmarks",
  "analyzing-sbom-for-supply-chain-vulnerabilities",
]);

// Domain color palette
const DOMAIN_COLORS = {
  "governance-risk-compliance": "#0ea5e9",
  "compliance-governance": "#0ea5e9",
  "privacy-compliance": "#0ea5e9",
  "cloud-security": "#8b5cf6",
  "threat-hunting": "#f59e0b",
  "threat-intelligence": "#f97316",
  "network-security": "#10b981",
  "web-application-security": "#06b6d4",
  "malware-analysis": "#ef4444",
  "digital-forensics": "#6366f1",
  "soc-operations": "#84cc16",
  "identity-access-management": "#ec4899",
  "identity-and-access-management": "#ec4899",
  "identity-security": "#ec4899",
  "container-security": "#14b8a6",
  "security-operations": "#64748b",
  "ot-ics-security": "#d97706",
  "api-security": "#7c3aed",
  "incident-response": "#dc2626",
  "vulnerability-management": "#059669",
  "red-teaming": "#b91c1c",
  "penetration-testing": "#991b1b",
  "devsecops": "#0284c7",
  "zero-trust-architecture": "#7c3aed",
  "zero-trust": "#7c3aed",
  "endpoint-security": "#0d9488",
  "phishing-defense": "#c026d3",
  "cryptography": "#4f46e5",
  "mobile-security": "#0891b2",
  "ransomware-defense": "#dc2626",
  "supply-chain-security": "#b45309",
  "deception-technology": "#9333ea",
  "ai-security": "#2563eb",
  "wireless-security": "#16a34a",
  "blockchain-security": "#f59e0b",
  "firmware-security": "#78716c",
  "general": "#6b7280",
};

const getDomainColor = (subdomain) =>
  DOMAIN_COLORS[subdomain] || "#6b7280";

const formatDomain = (s) =>
  s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Compact inline dataset (top skills for fast load; rest fetched async)
const SKILLS_SAMPLE = [
  {n:"performing-cmmc-level2-assessment",s:"governance-risk-compliance",d:"Conducts a CMMC 2.0 Level 2 readiness assessment evaluating all 110 NIST SP 800-171 practices. Generates SSP, POA&M, and SPRS score for DIB organizations.",custom:true},
  {n:"fedramp-authorization-preparation",s:"governance-risk-compliance",d:"Guides CSPs through FedRAMP Moderate/High authorization: SSP development, ConMon setup, 3PAO audit readiness against NIST 800-53 Rev 5.",custom:true},
  {n:"vendor-risk-management-program",s:"governance-risk-compliance",d:"Builds a TPRM/VRM program aligned to NIST 800-161 and ISO 27036. Covers vendor tiering, questionnaires, contract clauses, and continuous monitoring.",custom:true},
  {n:"performing-hipaa-security-risk-analysis",s:"governance-risk-compliance",d:"HIPAA Security Rule risk analysis per 45 CFR 164.308 for covered entities and business associates. Produces risk register and OCR-ready documentation.",custom:true},
  {n:"conducting-nist-800-171-assessment",s:"governance-risk-compliance",d:"Assesses all 110 NIST SP 800-171 requirements for CUI protection. SPRS score calculation, SSP/POA&M, and DFARS self-attestation guidance.",custom:true},
  {n:"performing-nist-csf-maturity-assessment",s:"compliance-governance",d:"Conducts a maturity assessment against NIST CSF 2.0 using Implementation Tiers to measure organizational cybersecurity posture and create improvement roadmaps."},
  {n:"implementing-iso-27001-information-security-management",s:"compliance-governance",d:"Covers the complete ISO/IEC 27001:2022 lifecycle from scoping through certification, including Annex A control selection and risk assessment methodology."},
  {n:"performing-soc2-type2-audit-preparation",s:"governance-risk-compliance",d:"Automates SOC 2 Type II audit preparation including gap assessment against AICPA Trust Services Criteria (CC1-CC9) and continuous compliance monitoring."},
  {n:"implementing-pci-dss-compliance-controls",s:"compliance-governance",d:"Implements PCI-DSS v4.0 controls across cardholder data environment including network segmentation, access control, and logging requirements."},
  {n:"implementing-gdpr-data-protection-controls",s:"privacy-compliance",d:"Implements GDPR data protection controls including data mapping, consent management, breach notification procedures, and DPA requirements."},
  {n:"performing-privacy-impact-assessment",s:"privacy-compliance",d:"Conducts privacy impact assessments (PIA/DPIA) for new systems, third-party integrations, and high-risk processing activities under GDPR and CCPA."},
  {n:"building-identity-governance-lifecycle-process",s:"identity-access-management",d:"Designs identity governance lifecycle covering provisioning, access reviews, role mining, segregation of duties, and automated deprovisioning workflows."},
  {n:"implementing-identity-governance-with-sailpoint",s:"identity-access-management",d:"Implements SailPoint IdentityIQ/IdentityNow for access certification campaigns, role management, and entitlement reviews across enterprise systems."},
  {n:"building-incident-response-playbook",s:"incident-response",d:"Designs structured incident response playbooks aligned to NIST SP 800-61r3 and SANS PICERL, with decision trees, RACI matrices, and SOAR integration."},
  {n:"auditing-cloud-with-cis-benchmarks",s:"cloud-security",d:"Conducts cloud security audits using CIS Foundations Benchmarks for AWS, Azure, and GCP using Prowler and ScoutSuite for automated assessment."},
  {n:"analyzing-sbom-for-supply-chain-vulnerabilities",s:"supply-chain-security",d:"Parses SBOMs in CycloneDX and SPDX JSON formats to identify supply chain vulnerabilities by correlating against NVD CVE database."},
  {n:"implementing-beyondcorp-zero-trust-access-model",s:"zero-trust-architecture",d:"Implements Google BeyondCorp zero trust model with device trust, identity-aware proxies, and context-aware access policies for enterprise resources."},
  {n:"implementing-gdpr-data-subject-access-request",s:"privacy-compliance",d:"Automates GDPR Data Subject Access Request (DSAR) workflows including identity verification, data discovery across systems, and 30-day response timelines."},
  {n:"implementing-nerc-cip-compliance-controls",s:"compliance-governance",d:"Implements NERC CIP reliability standards for bulk electric system cyber assets, covering electronic security perimeters and incident reporting."},
  {n:"auditing-aws-s3-bucket-permissions",s:"cloud-security",d:"Systematically audits AWS S3 bucket permissions to identify publicly accessible buckets, overly permissive ACLs, and missing encryption settings."},
  {n:"auditing-azure-active-directory-configuration",s:"cloud-security",d:"Audits Microsoft Entra ID configuration to identify risky authentication policies, overly permissive role assignments, stale accounts, and conditional access gaps."},
  {n:"auditing-gcp-iam-permissions",s:"cloud-security",d:"Audits GCP IAM permissions to identify overly permissive bindings, primitive role usage, service account key proliferation, and cross-project access risks."},
  {n:"implementing-aws-config-rules-for-compliance",s:"cloud-security",d:"Implements AWS Config managed and custom rules for continuous compliance monitoring against CIS benchmarks, PCI-DSS, and HIPAA Security Rule requirements."},
  {n:"implementing-aws-security-hub-compliance",s:"cloud-security",d:"Configures AWS Security Hub for centralized compliance monitoring with automated findings aggregation and remediation workflows."},
  {n:"analyzing-apt-group-with-mitre-navigator",s:"threat-intelligence",d:"Analyze APT group techniques using MITRE ATT&CK Navigator to create layered heatmaps of adversary TTPs for detection gap analysis."},
  {n:"analyzing-threat-actor-ttps-with-mitre-attack",s:"threat-intelligence",d:"MITRE ATT&CK knowledge base analysis for mapping adversary tactics, techniques, and procedures to defensive controls."},
  {n:"performing-nist-csf-maturity-assessment",s:"compliance-governance",d:"Full NIST CSF 2.0 maturity assessment across Govern, Identify, Protect, Detect, Respond, and Recover functions with Implementation Tier scoring."},
  {n:"implementing-device-posture-assessment-in-zero-trust",s:"zero-trust-architecture",d:"Implements device posture checks in zero trust architectures to enforce conditional access based on device compliance, patch status, and security posture."},
  {n:"implementing-browser-isolation-for-zero-trust",s:"zero-trust-architecture",d:"Deploys browser isolation solutions for zero trust web access, preventing malware and data exfiltration from web-based threats."},
  {n:"analyzing-security-logs-with-splunk",s:"incident-response",d:"Leverages Splunk Enterprise Security and SPL to investigate security incidents, build correlation searches, and create operational dashboards."},
  {n:"implementing-policy-as-code-with-open-policy-agent",s:"devsecops",d:"Implements OPA Gatekeeper for policy-as-code enforcement in Kubernetes clusters and CI/CD pipelines to ensure compliance at deployment time."},
  {n:"analyzing-kubernetes-audit-logs",s:"container-security",d:"Parses Kubernetes API server audit logs to detect exec-into-pod, secret access, RBAC modifications, and anomalous API call patterns."},
  {n:"auditing-kubernetes-cluster-rbac",s:"container-security",d:"Audits Kubernetes RBAC to identify overly permissive roles, wildcard permissions, dangerous ClusterRoleBindings, and privilege escalation paths."},
  {n:"implementing-endpoint-dlp-controls",s:"endpoint-security",d:"Deploys endpoint DLP controls to prevent unauthorized data exfiltration via USB, email, cloud uploads, and print channels."},
  {n:"hunting-for-supply-chain-compromise",s:"supply-chain-security",d:"Threat hunting methodology for detecting supply chain compromises in software dependencies, build systems, and update mechanisms."},
  {n:"detecting-supply-chain-attacks-in-ci-cd",s:"supply-chain-security",d:"Detects supply chain attacks in CI/CD pipelines by monitoring for dependency confusion, typosquatting, and malicious package injection."},
  {n:"implementing-supply-chain-security-with-in-toto",s:"supply-chain-security",d:"Implements the in-toto framework for software supply chain security, providing cryptographic attestations for each step in the build pipeline."},
  {n:"performing-active-directory-vulnerability-assessment",s:"identity-access-management",d:"Assesses Active Directory for security vulnerabilities including Kerberoasting, AS-REP roasting, DCSync rights, and privileged account exposure."},
  {n:"analyzing-active-directory-acl-abuse",s:"identity-security",d:"Detect dangerous ACL misconfigurations in Active Directory using ldap3 to identify GenericAll, WriteDACL, and WriteOwner abuse paths."},
  {n:"analyzing-office365-audit-logs-for-compromise",s:"cloud-security",d:"Parses Office 365 Unified Audit Logs via Microsoft Graph API to detect email forwarding rule creation, inbox delegation, and BEC indicators."},
  {n:"analyzing-azure-activity-logs-for-threats",s:"security-operations",d:"Queries Azure Monitor activity logs and sign-in logs to detect suspicious administrative operations, impossible travel, and privilege escalation."},
];

// Additional skills loaded from upstream GitHub API
const GITHUB_API_BASE = "https://api.github.com/repos/mukul975/Anthropic-Cybersecurity-Skills/contents/skills";

const ALL_SUBDOMAINS = [
  "governance-risk-compliance","compliance-governance","privacy-compliance",
  "cloud-security","threat-hunting","threat-intelligence","network-security",
  "web-application-security","malware-analysis","digital-forensics","soc-operations",
  "identity-access-management","container-security","security-operations",
  "ot-ics-security","api-security","incident-response","vulnerability-management",
  "red-teaming","penetration-testing","devsecops","zero-trust-architecture",
  "endpoint-security","phishing-defense","cryptography","mobile-security",
  "ransomware-defense","supply-chain-security","deception-technology","ai-security",
];

// ─── Main App ──────────────────────────────────────────────────────────────
export default function CyberEyeSkillsBrowser() {
  const [skills] = useState(SKILLS_SAMPLE);
  const [search, setSearch] = useState("");
  const [activeDomain, setActiveDomain] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all"); // all | cybereye | grc | upstream
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const searchRef = useRef(null);

  // Domain counts
  const domainCounts = useMemo(() => {
    const counts = { all: skills.length };
    skills.forEach((s) => {
      counts[s.s] = (counts[s.s] || 0) + 1;
    });
    return counts;
  }, [skills]);

  // Filtered skills
  const filtered = useMemo(() => {
    let list = skills;
    if (activeFilter === "cybereye") list = list.filter((s) => CYBEREYE_CUSTOM_NAMES.has(s.n));
    else if (activeFilter === "grc") list = list.filter((s) => GRC_PRIORITY.has(s.n));
    if (activeDomain !== "all") list = list.filter((s) => s.s === activeDomain);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (s) =>
          s.n.includes(q) ||
          s.s.includes(q) ||
          (s.d && s.d.toLowerCase().includes(q))
      );
    }
    return list;
  }, [skills, search, activeDomain, activeFilter]);

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape") {
        setSelectedSkill(null);
        setSearch("");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const getGithubUrl = (skillName) =>
    `https://github.com/CyberEyeSolutions/Anthropic-Cybersecurity-Skills/tree/main/skills/${skillName}`;

  return (
    <div style={styles.root}>
      {/* Ambient background */}
      <div style={styles.ambientBg} />

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.logoBlock}>
            <div style={styles.logoIcon}>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <polygon points="14,2 26,8 26,20 14,26 2,20 2,8" fill="none" stroke="#0ea5e9" strokeWidth="1.5"/>
                <polygon points="14,6 22,10 22,18 14,22 6,18 6,10" fill="none" stroke="#0ea5e9" strokeWidth="1" opacity="0.5"/>
                <circle cx="14" cy="14" r="3" fill="#0ea5e9"/>
              </svg>
            </div>
            <div>
              <div style={styles.logoName}>CyberEye Solutions</div>
              <div style={styles.logoSub}>Anthropic Cybersecurity Skills Library</div>
            </div>
          </div>
          <div style={styles.headerStats}>
            <StatPill label="Total Skills" value="759" color="#0ea5e9" />
            <StatPill label="CyberEye Custom" value="5" color="#f59e0b" />
            <StatPill label="Domains" value="30+" color="#8b5cf6" />
          </div>
        </div>

        {/* Search bar */}
        <div style={styles.searchRow}>
          <div style={styles.searchWrap}>
            <svg style={styles.searchIcon} width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="7" cy="7" r="5" stroke="#64748b" strokeWidth="1.5"/>
              <path d="M11 11l3 3" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              ref={searchRef}
              style={styles.searchInput}
              placeholder="Search 759 skills… (⌘K)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button style={styles.clearBtn} onClick={() => setSearch("")}>✕</button>
            )}
          </div>

          {/* Filter pills */}
          <div style={styles.filterRow}>
            {[
              { id: "all", label: "All Skills" },
              { id: "grc", label: "⭐ GRC Pack" },
              { id: "cybereye", label: "🔷 CyberEye Custom" },
            ].map((f) => (
              <button
                key={f.id}
                style={{
                  ...styles.filterPill,
                  ...(activeFilter === f.id ? styles.filterPillActive : {}),
                }}
                onClick={() => setActiveFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div style={styles.body}>
        {/* Sidebar */}
        <aside style={styles.sidebar}>
          <div style={styles.sidebarTitle}>Domains</div>
          <button
            style={{
              ...styles.domainItem,
              ...(activeDomain === "all" ? styles.domainItemActive : {}),
            }}
            onClick={() => setActiveDomain("all")}
          >
            <span style={styles.domainDot(getDomainColor("general"))} />
            <span style={styles.domainLabel}>All Domains</span>
            <span style={styles.domainCount}>{domainCounts.all}</span>
          </button>
          {ALL_SUBDOMAINS.filter((d) => domainCounts[d]).map((domain) => (
            <button
              key={domain}
              style={{
                ...styles.domainItem,
                ...(activeDomain === domain ? styles.domainItemActive : {}),
              }}
              onClick={() => setActiveDomain(domain)}
            >
              <span style={styles.domainDot(getDomainColor(domain))} />
              <span style={styles.domainLabel}>{formatDomain(domain)}</span>
              <span style={styles.domainCount}>{domainCounts[domain] || 0}</span>
            </button>
          ))}
        </aside>

        {/* Main content */}
        <main style={styles.main}>
          <div style={styles.resultsHeader}>
            <span style={styles.resultsCount}>
              {filtered.length} skill{filtered.length !== 1 ? "s" : ""}
              {search && <span style={styles.resultsQuery}> matching "{search}"</span>}
            </span>
            {activeDomain !== "all" && (
              <button
                style={styles.clearDomain}
                onClick={() => setActiveDomain("all")}
              >
                ✕ {formatDomain(activeDomain)}
              </button>
            )}
          </div>

          {filtered.length === 0 ? (
            <div style={styles.empty}>
              <div style={styles.emptyIcon}>🔍</div>
              <div style={styles.emptyText}>No skills found for "{search}"</div>
              <button style={styles.emptyReset} onClick={() => { setSearch(""); setActiveDomain("all"); setActiveFilter("all"); }}>
                Reset filters
              </button>
            </div>
          ) : (
            <div style={styles.grid}>
              {filtered.map((skill) => (
                <SkillCard
                  key={skill.n}
                  skill={skill}
                  isCustom={CYBEREYE_CUSTOM_NAMES.has(skill.n)}
                  isGrc={GRC_PRIORITY.has(skill.n)}
                  isHovered={hoveredSkill === skill.n}
                  isSelected={selectedSkill?.n === skill.n}
                  onHover={() => setHoveredSkill(skill.n)}
                  onLeave={() => setHoveredSkill(null)}
                  onClick={() => setSelectedSkill(selectedSkill?.n === skill.n ? null : skill)}
                  githubUrl={getGithubUrl(skill.n)}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Detail panel */}
      {selectedSkill && (
        <DetailPanel
          skill={selectedSkill}
          isCustom={CYBEREYE_CUSTOM_NAMES.has(selectedSkill.n)}
          onClose={() => setSelectedSkill(null)}
          githubUrl={getGithubUrl(selectedSkill.n)}
        />
      )}
    </div>
  );
}

// ─── Skill Card ───────────────────────────────────────────────────────────
function SkillCard({ skill, isCustom, isGrc, isHovered, isSelected, onHover, onLeave, onClick, githubUrl }) {
  const color = getDomainColor(skill.s);
  const title = skill.n.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div
      style={{
        ...styles.card,
        ...(isHovered || isSelected ? styles.cardHovered : {}),
        ...(isSelected ? styles.cardSelected : {}),
        borderTopColor: color,
      }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onClick}
    >
      {/* Badges */}
      <div style={styles.cardBadges}>
        {isCustom && (
          <span style={{ ...styles.badge, background: "rgba(245,158,11,0.15)", color: "#f59e0b", border: "1px solid rgba(245,158,11,0.3)" }}>
            🔷 CyberEye
          </span>
        )}
        {isGrc && !isCustom && (
          <span style={{ ...styles.badge, background: "rgba(14,165,233,0.1)", color: "#0ea5e9", border: "1px solid rgba(14,165,233,0.25)" }}>
            ⭐ GRC
          </span>
        )}
      </div>

      {/* Domain tag */}
      <div style={{ ...styles.domainTag, color, background: `${color}18`, border: `1px solid ${color}30` }}>
        {formatDomain(skill.s)}
      </div>

      {/* Title */}
      <div style={styles.cardTitle}>{title}</div>

      {/* Description */}
      {skill.d && (
        <div style={styles.cardDesc}>{skill.d}</div>
      )}

      {/* Footer */}
      <div style={styles.cardFooter}>
        <span style={styles.cardSlug}>{skill.n}</span>
        <a
          href={githubUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.cardLink}
          onClick={(e) => e.stopPropagation()}
        >
          GitHub →
        </a>
      </div>
    </div>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────
function DetailPanel({ skill, isCustom, onClose, githubUrl }) {
  const color = getDomainColor(skill.s);
  const title = skill.n.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const isGrc = GRC_PRIORITY.has(skill.n);

  const skillPath = isCustom
    ? `cybereye-skills/skills/${skill.n}/SKILL.md`
    : `skills/${skill.n}/SKILL.md`;

  return (
    <div style={styles.detailOverlay} onClick={onClose}>
      <div style={{ ...styles.detailPanel, borderTopColor: color }} onClick={(e) => e.stopPropagation()}>
        <button style={styles.detailClose} onClick={onClose}>✕</button>

        <div style={styles.detailHeader}>
          <div style={{ ...styles.domainTag, color, background: `${color}18`, border: `1px solid ${color}30`, marginBottom: 12 }}>
            {formatDomain(skill.s)}
          </div>
          {isCustom && (
            <span style={{ ...styles.badge, background: "rgba(245,158,11,0.15)", color: "#f59e0b", border: "1px solid rgba(245,158,11,0.3)", marginBottom: 8 }}>
              🔷 CyberEye Custom Skill
            </span>
          )}
          {isGrc && !isCustom && (
            <span style={{ ...styles.badge, background: "rgba(14,165,233,0.1)", color: "#0ea5e9", border: "1px solid rgba(14,165,233,0.25)", marginBottom: 8 }}>
              ⭐ CyberEye GRC Pack
            </span>
          )}
          <h2 style={styles.detailTitle}>{title}</h2>
          <p style={styles.detailDesc}>{skill.d}</p>
        </div>

        <div style={styles.detailMeta}>
          <MetaRow label="Skill ID" value={skill.n} mono />
          <MetaRow label="Domain" value={formatDomain(skill.s)} />
          <MetaRow label="Source" value={isCustom ? "CyberEye Solutions (custom)" : "mukul975/Anthropic-Cybersecurity-Skills"} />
          <MetaRow label="Path" value={skillPath} mono />
        </div>

        <div style={styles.detailActions}>
          <a
            href={githubUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.actionBtn}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style={{ marginRight: 6 }}>
              <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
            </svg>
            View on GitHub
          </a>
          <button
            style={{ ...styles.actionBtn, ...styles.actionBtnSecondary }}
            onClick={() => navigator.clipboard?.writeText(skill.n)}
          >
            Copy Skill ID
          </button>
        </div>

        {isCustom && (
          <div style={styles.customNote}>
            <div style={styles.customNoteTitle}>📁 CyberEye Fork</div>
            <div style={styles.customNoteText}>
              This skill was authored by CyberEye Solutions and lives in your fork at{" "}
              <code style={styles.inlineCode}>cybereye-skills/skills/{skill.n}/SKILL.md</code>.
              It is not in the upstream repository.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div style={{ ...styles.statPill, borderColor: `${color}40` }}>
      <span style={{ ...styles.statValue, color }}>{value}</span>
      <span style={styles.statLabel}>{label}</span>
    </div>
  );
}

function MetaRow({ label, value, mono }) {
  return (
    <div style={styles.metaRow}>
      <span style={styles.metaLabel}>{label}</span>
      <span style={{ ...styles.metaValue, ...(mono ? styles.metaMono : {}) }}>{value}</span>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────
const styles = {
  root: {
    minHeight: "100vh",
    background: "#060b14",
    color: "#e2e8f0",
    fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
    position: "relative",
    overflow: "hidden",
  },
  ambientBg: {
    position: "fixed",
    inset: 0,
    background: `
      radial-gradient(ellipse 80% 50% at 20% -10%, rgba(14,165,233,0.07) 0%, transparent 60%),
      radial-gradient(ellipse 60% 40% at 80% 110%, rgba(139,92,246,0.06) 0%, transparent 60%)
    `,
    pointerEvents: "none",
    zIndex: 0,
  },
  header: {
    position: "relative",
    zIndex: 10,
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    padding: "20px 24px 0",
    background: "rgba(6,11,20,0.9)",
    backdropFilter: "blur(12px)",
  },
  headerInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  logoBlock: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  logoIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoName: {
    fontSize: 16,
    fontWeight: 700,
    color: "#f1f5f9",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  logoSub: {
    fontSize: 11,
    color: "#475569",
    letterSpacing: "0.08em",
    marginTop: 2,
  },
  headerStats: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  statPill: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "6px 14px",
    border: "1px solid",
    borderRadius: 6,
    background: "rgba(255,255,255,0.03)",
    minWidth: 64,
  },
  statValue: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: 9,
    color: "#64748b",
    marginTop: 3,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    paddingBottom: 16,
    flexWrap: "wrap",
  },
  searchWrap: {
    position: "relative",
    flex: "1 1 280px",
    maxWidth: 480,
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    padding: "9px 36px 9px 36px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    color: "#e2e8f0",
    fontSize: 13,
    fontFamily: "'IBM Plex Mono', monospace",
    outline: "none",
    boxSizing: "border-box",
  },
  clearBtn: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    color: "#64748b",
    cursor: "pointer",
    fontSize: 12,
    padding: 2,
  },
  filterRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  filterPill: {
    padding: "6px 12px",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 20,
    color: "#94a3b8",
    fontSize: 11,
    cursor: "pointer",
    fontFamily: "'IBM Plex Mono', monospace",
    letterSpacing: "0.03em",
    transition: "all 0.15s",
    whiteSpace: "nowrap",
  },
  filterPillActive: {
    background: "rgba(14,165,233,0.15)",
    borderColor: "rgba(14,165,233,0.4)",
    color: "#0ea5e9",
  },
  body: {
    display: "flex",
    position: "relative",
    zIndex: 1,
    minHeight: "calc(100vh - 130px)",
  },
  sidebar: {
    width: 220,
    flexShrink: 0,
    padding: "16px 12px",
    borderRight: "1px solid rgba(255,255,255,0.06)",
    overflowY: "auto",
    maxHeight: "calc(100vh - 130px)",
    position: "sticky",
    top: 0,
  },
  sidebarTitle: {
    fontSize: 10,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#475569",
    marginBottom: 8,
    padding: "0 4px",
  },
  domainItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "6px 8px",
    background: "none",
    border: "none",
    borderRadius: 6,
    color: "#94a3b8",
    cursor: "pointer",
    fontSize: 11,
    fontFamily: "'IBM Plex Mono', monospace",
    textAlign: "left",
    transition: "background 0.1s",
    marginBottom: 1,
  },
  domainItemActive: {
    background: "rgba(255,255,255,0.06)",
    color: "#e2e8f0",
  },
  domainDot: (color) => ({
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: color,
    flexShrink: 0,
  }),
  domainLabel: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 11,
  },
  domainCount: {
    fontSize: 10,
    color: "#475569",
    flexShrink: 0,
  },
  main: {
    flex: 1,
    padding: "16px 20px",
    overflowY: "auto",
    maxHeight: "calc(100vh - 130px)",
  },
  resultsHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },
  resultsCount: {
    fontSize: 12,
    color: "#64748b",
  },
  resultsQuery: {
    color: "#94a3b8",
  },
  clearDomain: {
    padding: "3px 10px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 20,
    color: "#94a3b8",
    fontSize: 11,
    cursor: "pointer",
    fontFamily: "'IBM Plex Mono', monospace",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 12,
  },
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderTop: "2px solid",
    borderRadius: 10,
    padding: "14px 16px",
    cursor: "pointer",
    transition: "all 0.15s",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minHeight: 160,
  },
  cardHovered: {
    background: "rgba(255,255,255,0.05)",
    borderColor: "rgba(255,255,255,0.14)",
    transform: "translateY(-1px)",
  },
  cardSelected: {
    background: "rgba(14,165,233,0.06)",
    borderColor: "rgba(14,165,233,0.2)",
  },
  cardBadges: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    minHeight: 20,
  },
  badge: {
    fontSize: 10,
    padding: "2px 8px",
    borderRadius: 20,
    fontWeight: 600,
    letterSpacing: "0.04em",
  },
  domainTag: {
    display: "inline-block",
    fontSize: 10,
    padding: "2px 8px",
    borderRadius: 4,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    alignSelf: "flex-start",
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#e2e8f0",
    lineHeight: 1.4,
    textTransform: "capitalize",
  },
  cardDesc: {
    fontSize: 11,
    color: "#64748b",
    lineHeight: 1.6,
    flex: 1,
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "auto",
    paddingTop: 8,
    borderTop: "1px solid rgba(255,255,255,0.05)",
  },
  cardSlug: {
    fontSize: 9,
    color: "#334155",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "70%",
  },
  cardLink: {
    fontSize: 10,
    color: "#0ea5e9",
    textDecoration: "none",
    flexShrink: 0,
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 80,
    gap: 12,
  },
  emptyIcon: {
    fontSize: 40,
  },
  emptyText: {
    color: "#64748b",
    fontSize: 14,
  },
  emptyReset: {
    padding: "8px 18px",
    background: "rgba(14,165,233,0.1)",
    border: "1px solid rgba(14,165,233,0.3)",
    borderRadius: 6,
    color: "#0ea5e9",
    cursor: "pointer",
    fontSize: 12,
    fontFamily: "'IBM Plex Mono', monospace",
    marginTop: 8,
  },
  // Detail panel
  detailOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    zIndex: 100,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    backdropFilter: "blur(4px)",
  },
  detailPanel: {
    width: 420,
    maxWidth: "100vw",
    height: "100vh",
    background: "#0a1628",
    borderLeft: "1px solid rgba(255,255,255,0.1)",
    borderTop: "3px solid",
    overflowY: "auto",
    padding: 28,
    position: "relative",
  },
  detailClose: {
    position: "absolute",
    top: 16,
    right: 16,
    background: "none",
    border: "none",
    color: "#64748b",
    cursor: "pointer",
    fontSize: 16,
    padding: 4,
  },
  detailHeader: {
    marginBottom: 24,
    paddingTop: 8,
  },
  detailTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#f1f5f9",
    lineHeight: 1.3,
    margin: "0 0 12px",
    textTransform: "capitalize",
  },
  detailDesc: {
    fontSize: 12,
    color: "#94a3b8",
    lineHeight: 1.7,
    margin: 0,
  },
  detailMeta: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 8,
    padding: "12px 14px",
    marginBottom: 16,
  },
  metaRow: {
    display: "flex",
    gap: 8,
    marginBottom: 8,
    fontSize: 11,
    alignItems: "flex-start",
  },
  metaLabel: {
    color: "#475569",
    width: 80,
    flexShrink: 0,
    paddingTop: 1,
  },
  metaValue: {
    color: "#94a3b8",
    flex: 1,
    wordBreak: "break-all",
  },
  metaMono: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 10,
    color: "#0ea5e9",
  },
  detailActions: {
    display: "flex",
    gap: 8,
    marginBottom: 20,
  },
  actionBtn: {
    display: "flex",
    alignItems: "center",
    padding: "8px 14px",
    background: "rgba(14,165,233,0.1)",
    border: "1px solid rgba(14,165,233,0.3)",
    borderRadius: 6,
    color: "#0ea5e9",
    fontSize: 12,
    cursor: "pointer",
    textDecoration: "none",
    fontFamily: "'IBM Plex Mono', monospace",
  },
  actionBtnSecondary: {
    background: "rgba(255,255,255,0.04)",
    borderColor: "rgba(255,255,255,0.1)",
    color: "#94a3b8",
  },
  customNote: {
    background: "rgba(245,158,11,0.06)",
    border: "1px solid rgba(245,158,11,0.2)",
    borderRadius: 8,
    padding: "12px 14px",
  },
  customNoteTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#f59e0b",
    marginBottom: 6,
  },
  customNoteText: {
    fontSize: 11,
    color: "#94a3b8",
    lineHeight: 1.6,
  },
  inlineCode: {
    background: "rgba(255,255,255,0.08)",
    padding: "1px 5px",
    borderRadius: 3,
    fontSize: 10,
    color: "#f59e0b",
  },
};
